import React, { CSSProperties } from "react";
import { Sponsor } from "./Sponsor.tsx";
import { useDebouncedCurrentGame } from "../../../../../hooks/useDebouncedCurrentGame.ts";
import { LedwallScore } from "./LedwallScore.tsx";
import { LedwallTime } from "./LedwallTime.tsx";
import { Text } from "./Text.tsx";
import { useFullscreen } from "@mantine/hooks";
import { ShootOut } from "./ShootOut.tsx";
import { ActivityItem } from "./ActivityItem.tsx";
import { useTeam } from "../../../../../hooks/useTeam.ts";
import { getPresentationParamsForOrigin } from "../../../../../getPresentationParamsForOrigin.ts";

export interface DimSpot {
  x: number;
  y: number;
}

interface DisplayProps {
  competitionId?: string;
  text?: string;
  activityItem?: ActivityItem;
  disabled?: boolean;
  dimSpot?: DimSpot | null;
  onDimSpotClick?: (spot: DimSpot) => void;
}

const wholeLedwallBoxStyle: CSSProperties = {
  zIndex: 1000,
  color: "white",
  fontFamily: "Chivo Mono",
  overflow: "hidden",
};

const singleLedwallBoxStyle: CSSProperties = {
  backgroundColor: "black",
  width: 1920,
  height: 144,
  left: 0,
  display: "flex",
};

const DIM_SPOT_RADIUS = 80;

export const Display: React.FC<DisplayProps> = (props) => {
  const {
    competitionId,
    text,
    disabled,
    activityItem,
    dimSpot,
    onDimSpotClick,
  } = props;

  const { fullscreen } = useFullscreen();
  const currentGame = useDebouncedCurrentGame(competitionId, 10 * 1000);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onDimSpotClick) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    onDimSpotClick({ x, y });
  };

  const homeTeam = useTeam(currentGame.data?.actualTeams.homeTeamId);
  const guestTeam = useTeam(currentGame.data?.actualTeams.guestTeamId);

  const competitionLedsponsor =
    getPresentationParamsForOrigin().ledwallSponsor?.["2x1920x144"];

  return (
    <div
      style={{
        ...wholeLedwallBoxStyle,
        position: fullscreen ? "absolute" : "relative",
        left: fullscreen ? 0 : undefined,
        top: fullscreen ? 0 : undefined,
        cursor: onDimSpotClick ? "crosshair" : undefined,
      }}
      onClick={handleClick}
    >
      {dimSpot && (
        <div
          style={{
            position: "absolute",
            left: `calc(${String(dimSpot.x * 100)}% - ${String(DIM_SPOT_RADIUS)}px)`,
            top: `calc(${String(dimSpot.y * 100)}% - ${String(DIM_SPOT_RADIUS)}px)`,
            width: DIM_SPOT_RADIUS * 2,
            height: DIM_SPOT_RADIUS * 2,
            borderRadius: "50%",
            backgroundColor: "black",
            zIndex: 2000,
            pointerEvents: "none",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: 0,
              height: 0,
              borderLeft: "10px solid transparent",
              borderRight: "10px solid transparent",
              borderBottom: "10px solid #1a1a1a",
            }}
          />
        </div>
      )}
      <div
        style={{
          ...singleLedwallBoxStyle,
        }}
      >
        {!disabled && (
          <>
            {text && <Text text={text} />}
            {activityItem && !text && (
              <ActivityItem activityItem={activityItem} />
            )}
            {!text && !activityItem && currentGame.isSuccess && (
              <>
                <Sponsor
                  fileURL={
                    competitionLedsponsor?.leftScreenFileURL ??
                    homeTeam.data?.sponsor?.ledwallFilePath
                  }
                />
                {currentGame.data.shootOutStarted ? (
                  <ShootOut team="home" gameId={currentGame.data.id} />
                ) : (
                  <LedwallTime gameId={currentGame.data.id} />
                )}
              </>
            )}
          </>
        )}
      </div>
      <div
        style={{
          ...singleLedwallBoxStyle,
          marginTop: fullscreen ? undefined : 1,
        }}
      >
        {!disabled && (
          <>
            {text && <Text text={text} />}
            {activityItem && !text && (
              <ActivityItem activityItem={activityItem} />
            )}
            {!text && !activityItem && currentGame.isSuccess && (
              <>
                {currentGame.data.shootOutStarted ? (
                  <ShootOut team="guest" gameId={currentGame.data.id} />
                ) : (
                  <LedwallScore gameId={currentGame.data.id} />
                )}
                <Sponsor
                  fileURL={
                    competitionLedsponsor?.rightScreenFileURL ??
                    guestTeam.data?.sponsor?.ledwallFilePath
                  }
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
